/**
 * Modification catalog - the full set of foot-region modifications
 * supported by the prescription form. Each entry mirrors the legacy
 * app's form fragments at `reference/insolech-web-app-current/src/components/forms/`
 * but normalized to a single declarative schema so the new form can
 * render them generically (with per-modification overrides for
 * specialty parameters like angle, depth, height).
 *
 * The catalog drives:
 *   - The cards rendered inside each region's section
 *   - The lightbox reference image path (Rafael will populate later;
 *     we use predictable filenames so a single rsync drop fills them in)
 *   - The Zod-validated parameters object on each modification
 *   - The mapping back into the existing `PrescriptionFormState.modifications`
 *     shape so the wizard's create_order_with_lines payload is unchanged
 */

export type Region = 'rearfoot' | 'midfoot' | 'forefoot' | 'offloading';

export interface ParameterSchema {
  key: string;
  label: string;
  type: 'number' | 'select' | 'side';
  /** when type === 'select' */
  options?: ReadonlyArray<{ value: string; label: string }>;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  /** Display in left/right columns. */
  side?: 'left' | 'right';
  required?: boolean;
}

export interface ModificationDefinition {
  id: string;
  region: Region;
  label: string;
  /**
   * Tooltip explaining the clinical purpose of the modification. Mirrors
   * the legacy app's TooltipContent fragments at a high level - Rafael
   * has more nuanced copy stored in markdown which can replace this.
   */
  description?: string;
  /**
   * Reference image path. Rafael will populate
   * `reference/brand-assets/prescription-references/<region>/<id>.jpg` later.
   * For now the path is predictable and the lightbox renders an empty state.
   */
  referenceImage?: string;
  /** Parameter schema rendered inside the card when the modification is enabled. */
  parameters?: ReadonlyArray<ParameterSchema>;
  /**
   * Whether this modification supports the standard Light/Medium/Strong
   * intensity selector. Default true.
   */
  supportsIntensity?: boolean;
}

const intensityOnly: ReadonlyArray<ParameterSchema> = [];

export const MODIFICATIONS: ReadonlyArray<ModificationDefinition> = [
  /* ============================================================ */
  /*  REARFOOT                                                    */
  /* ============================================================ */
  {
    id: 'heel_cushion',
    region: 'rearfoot',
    label: 'Heel cushion',
    description: 'Soft pad at the heel to absorb impact. Common for plantar fasciitis or heel-spur pain.',
    referenceImage: '/prescription-references/rearfoot/heel_cushion.jpg',
    parameters: [
      { key: 'depth_mm_left', label: 'Depth L', type: 'number', side: 'left', min: 0, max: 6, step: 0.5, unit: 'mm' },
      { key: 'depth_mm_right', label: 'Depth R', type: 'number', side: 'right', min: 0, max: 6, step: 0.5, unit: 'mm' },
    ],
  },
  {
    id: 'heel_raise',
    region: 'rearfoot',
    label: 'Heel raise',
    description: 'Adds height to the rear of the insole. Used to compensate leg-length discrepancy or to relieve Achilles tension.',
    referenceImage: '/prescription-references/rearfoot/heel_raise.jpg',
    parameters: [
      { key: 'height_mm_left', label: 'Height L', type: 'number', side: 'left', min: 0, max: 12, step: 0.5, unit: 'mm' },
      { key: 'height_mm_right', label: 'Height R', type: 'number', side: 'right', min: 0, max: 12, step: 0.5, unit: 'mm' },
    ],
  },
  {
    id: 'rearfoot_post',
    region: 'rearfoot',
    label: 'Posted heel post (extrinsic)',
    description: 'Wedge under the heel that biases pronation/supination. Medial = anti-pronation, lateral = anti-supination.',
    referenceImage: '/prescription-references/rearfoot/rearfoot_post.jpg',
    parameters: [
      {
        key: 'side_left',
        label: 'L side',
        type: 'select',
        side: 'left',
        options: [
          { value: 'medial', label: 'Medial' },
          { value: 'lateral', label: 'Lateral' },
        ],
      },
      { key: 'angle_deg_left', label: 'L angle', type: 'number', side: 'left', min: 0, max: 8, step: 0.5, unit: '°' },
      {
        key: 'side_right',
        label: 'R side',
        type: 'select',
        side: 'right',
        options: [
          { value: 'medial', label: 'Medial' },
          { value: 'lateral', label: 'Lateral' },
        ],
      },
      { key: 'angle_deg_right', label: 'R angle', type: 'number', side: 'right', min: 0, max: 8, step: 0.5, unit: '°' },
    ],
  },
  {
    id: 'intrinsic_supination_wedge',
    region: 'rearfoot',
    label: 'Intrinsic supination wedge',
    description: 'Standard 15° wedge. Adjust height to dial in correction (typical 1–5 mm).',
    referenceImage: '/prescription-references/rearfoot/intrinsic_supination.jpg',
    parameters: [
      { key: 'angle_deg_left', label: 'Angle L', type: 'number', side: 'left', min: 0, max: 30, step: 1, unit: '°' },
      { key: 'height_mm_left', label: 'Height L', type: 'number', side: 'left', min: 0, max: 10, step: 0.5, unit: 'mm' },
      { key: 'angle_deg_right', label: 'Angle R', type: 'number', side: 'right', min: 0, max: 30, step: 1, unit: '°' },
      { key: 'height_mm_right', label: 'Height R', type: 'number', side: 'right', min: 0, max: 10, step: 0.5, unit: 'mm' },
    ],
  },
  {
    id: 'intrinsic_pronation_wedge',
    region: 'rearfoot',
    label: 'Intrinsic pronation wedge',
    description: 'Counterpart to the supination wedge, biases ankle into pronation when needed.',
    referenceImage: '/prescription-references/rearfoot/intrinsic_pronation.jpg',
    parameters: [
      { key: 'angle_deg_left', label: 'Angle L', type: 'number', side: 'left', min: 0, max: 30, step: 1, unit: '°' },
      { key: 'height_mm_left', label: 'Height L', type: 'number', side: 'left', min: 0, max: 10, step: 0.5, unit: 'mm' },
      { key: 'angle_deg_right', label: 'Angle R', type: 'number', side: 'right', min: 0, max: 30, step: 1, unit: '°' },
      { key: 'height_mm_right', label: 'Height R', type: 'number', side: 'right', min: 0, max: 10, step: 0.5, unit: 'mm' },
    ],
  },
  {
    id: 'heel_stabilizer',
    region: 'rearfoot',
    label: 'Heel stabilizer',
    description: 'Deeper heel cup to control rearfoot motion. Useful for severe pronation or supination.',
    referenceImage: '/prescription-references/rearfoot/heel_stabilizer.jpg',
    supportsIntensity: false,
    parameters: intensityOnly,
  },

  /* ============================================================ */
  /*  MIDFOOT                                                     */
  /* ============================================================ */
  {
    id: 'arch_reinforcement',
    region: 'midfoot',
    label: 'Arch reinforcement',
    description: 'Increased medial-longitudinal arch support. Standard for over-pronation and PTTD.',
    referenceImage: '/prescription-references/midfoot/arch_reinforcement.jpg',
    parameters: [
      { key: 'height_mm_left', label: 'Height L', type: 'number', side: 'left', min: 0, max: 30, step: 1, unit: 'mm' },
      { key: 'height_mm_right', label: 'Height R', type: 'number', side: 'right', min: 0, max: 30, step: 1, unit: 'mm' },
    ],
  },
  {
    id: 'medial_flange',
    region: 'midfoot',
    label: 'Medial flange',
    description: 'Vertical wall on the medial side of the arch. Resists pronation collapse.',
    referenceImage: '/prescription-references/midfoot/medial_flange.jpg',
  },
  {
    id: 'lateral_flange',
    region: 'midfoot',
    label: 'Lateral flange',
    description: 'Vertical wall on the lateral side. Resists supination roll-out.',
    referenceImage: '/prescription-references/midfoot/lateral_flange.jpg',
  },
  {
    id: 'cuboid_pad',
    region: 'midfoot',
    label: 'Cuboid cushion pad',
    description: 'Cushioning under the cuboid. Helpful for cuboid syndrome and lateral-foot pain.',
    referenceImage: '/prescription-references/midfoot/cuboid_pad.jpg',
  },
  {
    id: 'fascial_accommodation',
    region: 'midfoot',
    label: 'Fascial accommodation',
    description: 'Cut-out / soft pocket in the medial arch to relieve plantar fascia traction points.',
    referenceImage: '/prescription-references/midfoot/fascial_accommodation.jpg',
  },
  {
    id: 'navicular_pad',
    region: 'midfoot',
    label: 'Navicular / scaphoid pad',
    description: 'Localized pad under the navicular. Used for accessory navicular and tibialis posterior insertion pain.',
    referenceImage: '/prescription-references/midfoot/navicular_pad.jpg',
  },

  /* ============================================================ */
  /*  FOREFOOT                                                    */
  /* ============================================================ */
  {
    id: 'metatarsal_pad',
    region: 'forefoot',
    label: 'Metatarsal pad',
    description: 'Dome behind metatarsal heads (most common modification). Relieves metatarsalgia and Morton\'s neuroma symptoms.',
    referenceImage: '/prescription-references/forefoot/met_pad.jpg',
    parameters: [
      { key: 'thickness_mm_left', label: 'Thickness L', type: 'number', side: 'left', min: 0, max: 8, step: 0.5, unit: 'mm' },
      { key: 'thickness_mm_right', label: 'Thickness R', type: 'number', side: 'right', min: 0, max: 8, step: 0.5, unit: 'mm' },
    ],
  },
  {
    id: 'morton_extension',
    region: 'forefoot',
    label: 'Morton extension',
    description: 'Forward extension of the insole under the 1st metatarsal head. Supports first-ray function.',
    referenceImage: '/prescription-references/forefoot/morton_extension.jpg',
    parameters: [
      { key: 'thickness_mm_left', label: 'Thickness L', type: 'number', side: 'left', min: 0, max: 6, step: 0.5, unit: 'mm' },
      { key: 'thickness_mm_right', label: 'Thickness R', type: 'number', side: 'right', min: 0, max: 6, step: 0.5, unit: 'mm' },
    ],
  },
  {
    id: 'kinetic_wedge',
    region: 'forefoot',
    label: 'Kinetic wedge',
    description: 'Wedge under the 1st metatarsal that promotes plantarflexion of the first ray.',
    referenceImage: '/prescription-references/forefoot/kinetic_wedge.jpg',
    parameters: [
      { key: 'angle_deg_left', label: 'Angle L', type: 'number', side: 'left', min: 0, max: 8, step: 0.5, unit: '°' },
      { key: 'angle_deg_right', label: 'Angle R', type: 'number', side: 'right', min: 0, max: 8, step: 0.5, unit: '°' },
    ],
  },
  {
    id: 'forefoot_post',
    region: 'forefoot',
    label: 'Forefoot posting',
    description: 'Wedge under the forefoot that biases pronation/supination.',
    referenceImage: '/prescription-references/forefoot/forefoot_post.jpg',
    parameters: [
      {
        key: 'side_left',
        label: 'L side',
        type: 'select',
        side: 'left',
        options: [
          { value: 'medial', label: 'Medial' },
          { value: 'lateral', label: 'Lateral' },
        ],
      },
      { key: 'angle_deg_left', label: 'L angle', type: 'number', side: 'left', min: 0, max: 6, step: 0.5, unit: '°' },
      {
        key: 'side_right',
        label: 'R side',
        type: 'select',
        side: 'right',
        options: [
          { value: 'medial', label: 'Medial' },
          { value: 'lateral', label: 'Lateral' },
        ],
      },
      { key: 'angle_deg_right', label: 'R angle', type: 'number', side: 'right', min: 0, max: 6, step: 0.5, unit: '°' },
    ],
  },
  {
    id: 'first_ray_cutout',
    region: 'forefoot',
    label: '1st ray cut-out',
    description: 'Sub-1st cut-out to allow the first metatarsal to drop, encouraging windlass mechanism activation.',
    referenceImage: '/prescription-references/forefoot/first_ray_cutout.jpg',
  },
  {
    id: 'gait_plate',
    region: 'forefoot',
    label: 'Gait plate',
    description: 'Asymmetric forefoot edge to encourage in-toeing or out-toeing in pediatric prescriptions.',
    referenceImage: '/prescription-references/forefoot/gait_plate.jpg',
    parameters: [
      {
        key: 'direction',
        label: 'Direction',
        type: 'select',
        options: [
          { value: 'in_toeing', label: 'In-toeing (medial)' },
          { value: 'out_toeing', label: 'Out-toeing (lateral)' },
        ],
      },
    ],
  },
  {
    id: 'toe_crest',
    region: 'forefoot',
    label: 'Toe crest',
    description: 'Pad under the lesser-toe sulcus that fills the space and reduces clawing.',
    referenceImage: '/prescription-references/forefoot/toe_crest.jpg',
  },

  /* ============================================================ */
  /*  OFFLOADING                                                  */
  /* ============================================================ */
  {
    id: 'horseshoe_offloading',
    region: 'offloading',
    label: 'Horseshoe offloading',
    description: 'U-shaped cut-out around a focal lesion (e.g. plantar wart, callus) to redistribute pressure off the lesion.',
    referenceImage: '/prescription-references/offloading/horseshoe.jpg',
    parameters: [
      {
        key: 'location',
        label: 'Location',
        type: 'select',
        options: [
          { value: 'heel', label: 'Heel' },
          { value: 'mid', label: 'Midfoot' },
          { value: '1st_met', label: '1st metatarsal head' },
          { value: '2nd_met', label: '2nd metatarsal head' },
          { value: '3rd_met', label: '3rd metatarsal head' },
          { value: '4th_met', label: '4th metatarsal head' },
          { value: '5th_met', label: '5th metatarsal head' },
        ],
      },
      {
        key: 'side',
        label: 'Side',
        type: 'select',
        options: [
          { value: 'left', label: 'Left foot' },
          { value: 'right', label: 'Right foot' },
          { value: 'both', label: 'Both' },
        ],
      },
    ],
  },
  {
    id: 'subcapital_support',
    region: 'offloading',
    label: 'Subcapital support',
    description: 'Pad just behind a metatarsal head to offload it.',
    referenceImage: '/prescription-references/offloading/subcapital.jpg',
    parameters: [
      { key: 'thickness_mm_left', label: 'Thickness L', type: 'number', side: 'left', min: 0, max: 8, step: 0.5, unit: 'mm' },
      { key: 'thickness_mm_right', label: 'Thickness R', type: 'number', side: 'right', min: 0, max: 8, step: 0.5, unit: 'mm' },
    ],
  },
  {
    id: 'extra_heel_cushion',
    region: 'offloading',
    label: 'Extra heel cushion',
    description: 'Additional shock-absorbing cushion at the heel for high-impact use cases.',
    referenceImage: '/prescription-references/offloading/extra_heel.jpg',
    parameters: [
      { key: 'depth_mm_left', label: 'Depth L', type: 'number', side: 'left', min: 0, max: 10, step: 0.5, unit: 'mm' },
      { key: 'depth_mm_right', label: 'Depth R', type: 'number', side: 'right', min: 0, max: 10, step: 0.5, unit: 'mm' },
    ],
  },
  {
    id: 'heel_spur_pad',
    region: 'offloading',
    label: 'Heel spur pad',
    description: 'Localized cushion + cut-out around a calcaneal spur.',
    referenceImage: '/prescription-references/offloading/heel_spur.jpg',
  },
  {
    id: 'ipj_discharge',
    region: 'offloading',
    label: 'IPJ discharge',
    description: 'Cut-out under interphalangeal joints. Useful for hammer-toe pressure points.',
    referenceImage: '/prescription-references/offloading/ipj.jpg',
  },
  {
    id: 'toe_pulp_offloading',
    region: 'offloading',
    label: 'Toe pulp offloading',
    description: 'Cut-out under the toe-tip pulps. For ulcer-prone diabetic feet or distal neuropathy.',
    referenceImage: '/prescription-references/offloading/toe_pulp.jpg',
  },
];

export const MODIFICATIONS_BY_REGION: Record<Region, ModificationDefinition[]> = {
  rearfoot: MODIFICATIONS.filter((m) => m.region === 'rearfoot'),
  midfoot: MODIFICATIONS.filter((m) => m.region === 'midfoot'),
  forefoot: MODIFICATIONS.filter((m) => m.region === 'forefoot'),
  offloading: MODIFICATIONS.filter((m) => m.region === 'offloading'),
};

export function getModification(id: string): ModificationDefinition | undefined {
  return MODIFICATIONS.find((m) => m.id === id);
}
